import { reportError, reportUsage } from '@ms/yammer-telemetry';
import { getFirstResource, getQueryParameter } from '@ms/yammer-web-support/dist/location';
import { useEffect } from 'react';

import { lazyReportMessageAttachmentClickedFromLinkClick } from './lazyReportMessageAttachmentClickedFromLinkClick';
import { getValidAlphanumericId, getValidCopyLinkValue, getValidNumberId } from './validations';

type UseReportPageLoadedFromLinkClick = (pathname: string) => void;

const getStringQueryParameter = (key: string) => {
  const queryParameter = getQueryParameter(key);

  if (Array.isArray(queryParameter)) {
    return queryParameter[0];
  }

  return queryParameter;
};

const getDecodedResourceId = (firstResource: string) => {
  try {
    // oxlint-disable-next-line eslint/no-restricted-globals
    const decodedResource = JSON.parse(atob(firstResource)) as { readonly id?: string };

    return decodedResource.id;
  } catch (error) {
    reportError({
      error,
      eventProperties: {
        errorCode: 'DecodedResourceIdFailed',
      },
    });
  }
};

export const useReportPageLoadedFromLinkClick: UseReportPageLoadedFromLinkClick = (pathname) => {
  const isNotificationsUnsubscribePage = pathname.includes('/notifications/unsubscribe');
  const emailType = getStringQueryParameter('email_type');
  const followedUser = getStringQueryParameter('followed_user');

  const integrationOrigin = getStringQueryParameter('trk_integration_origin');
  const notifId = getStringQueryParameter('trk_notif_id');
  const outlookOrigin = getStringQueryParameter('trk_outlook_origin');
  let subEvent = getStringQueryParameter('trk_event');
  if (isNotificationsUnsubscribePage) {
    subEvent = followedUser ? 'unsubscribe_page' : 'notification_settings_page';
  }
  const scenarioType = isNotificationsUnsubscribePage ? emailType : getStringQueryParameter('trk_scenario_type');
  const threadId = getStringQueryParameter('trk_thread_id');
  const groupId = getStringQueryParameter('trk_group_id');
  const storylineUserId = getStringQueryParameter('trk_storyline_user_id');
  const scenarioSubType = getStringQueryParameter('trk_scenario_sub_type');
  const copyLink = getStringQueryParameter('trk_copy_link');
  const partnerOrigin = getStringQueryParameter('trk_partner_origin');
  const partnerOriginSource = getStringQueryParameter('trk_partner_origin_source');
  const origin = integrationOrigin ?? outlookOrigin;
  const shouldIncludePartnerOrigin = !!(partnerOrigin && partnerOriginSource);
  const shouldIncludeDestination = shouldIncludePartnerOrigin;
  const shouldReportPageLoad = !!(origin && scenarioType && subEvent) || !!copyLink || shouldIncludePartnerOrigin;

  useEffect(() => {
    if (shouldReportPageLoad) {
      const firstResource = getFirstResource(pathname);
      const decodedResourceId = firstResource ? getDecodedResourceId(firstResource) : undefined;
      const validResourceId = getValidNumberId(decodedResourceId);

      const validThreadId = getValidNumberId(threadId);
      const validGroupId = getValidNumberId(groupId);
      const validNotificationId = getValidAlphanumericId(notifId);
      const validCopyLink = getValidCopyLinkValue(copyLink);
      const attachmentId = getStringQueryParameter('trk_attachment_id');
      const validAttachmentId = getValidNumberId(attachmentId);

      reportUsage({
        eventName: 'page_loaded_from_link_click',
        eventProperties: {
          ...(storylineUserId && { storylineUserId }),
          ...(scenarioType && { scenarioType }),
          ...(subEvent && { subEvent }),
          ...(origin && { integrationOrigin: origin }),

          ...(notifId && { notif_id: validNotificationId }),
          ...(threadId && { threadId: validThreadId }),
          ...(groupId && { groupId: validGroupId }),
          ...(scenarioSubType && { scenarioSubType }),
          ...(copyLink && { copyLink: validCopyLink }),
          ...(decodedResourceId && { resourceId: validResourceId }),
          ...(shouldIncludePartnerOrigin && { partnerOrigin, partnerOriginSource }),
          ...(shouldIncludeDestination && { destination: pathname }),
        },
      });

      void lazyReportMessageAttachmentClickedFromLinkClick({
        subEvent,
        attachmentId: validAttachmentId,
        resourceId: validResourceId,
        threadId: validThreadId,
      });
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, []);
};
